import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { InvestorSectionIcon } from "./InvestorMetricIcons";
import {
  FUNDED_PROJECTS_LABEL,
  FUNDRAISING_PROJECTS_LABEL,
  isRedeemSharesRoute,
  isTransferableProjectsRoute,
  REDEEMABLE_PROJECTS_LABEL,
  TRANSFERABLE_PROJECTS_LABEL,
} from "../../utils/investorNavConfig";

function SharesMarketTabLabel({ icon, label, count, showCount = true }) {
  const displayCount = showCount || Number(count) > 0;
  return (
    <span className="invx-tab-label">
      {icon ? (
        <span className="invx-tab-icon" aria-hidden>
          <InvestorSectionIcon name={icon} />
        </span>
      ) : null}
      <span className="invx-tab-text">{label}</span>
      {displayCount ? <span className="invx-tab-count">{count ?? 0}</span> : null}
    </span>
  );
}

const MARKET_VIEWS = [
  { id: "fundraising", label: FUNDRAISING_PROJECTS_LABEL, icon: "fundraising", countKey: "fundraising" },
  { id: "funded", label: FUNDED_PROJECTS_LABEL, icon: "funded", countKey: "funded" },
];

const ROUTE_TABS = [
  {
    path: "/investments/transfers",
    label: TRANSFERABLE_PROJECTS_LABEL,
    icon: "transfer",
    countKey: "transferable",
    isActive: isTransferableProjectsRoute,
  },
  {
    path: "/investments/redemptions",
    label: REDEEMABLE_PROJECTS_LABEL,
    icon: "redeemable",
    countKey: "redeemPending",
    isActive: isRedeemSharesRoute,
  },
];

/** Shares Management: Fundraising / Funded (catalog views) + Transferable / Redeemable (dedicated routes). */
export default function SharesManagementMarketTabs({
  activeView,
  onViewChange,
  counts = { fundraising: 0, funded: 0, transferable: 0, redeemPending: 0 },
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const onDedicatedRoute = ROUTE_TABS.some((t) => t.isActive(location.pathname));

  const selectMarketView = (viewId) => {
    if (onDedicatedRoute) {
      const params = new URLSearchParams();
      params.set("view", viewId);
      navigate(`/investments?${params.toString()}`);
      return;
    }
    onViewChange(viewId);
  };

  return (
    <div className="invx-tabs shares-management-market-tabs" role="tablist" aria-label="Shares management views">
      {MARKET_VIEWS.map((tab) => {
        const active = !onDedicatedRoute && activeView === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            className={`invx-tab${active ? " active" : ""}`}
            onClick={() => selectMarketView(tab.id)}
          >
            <SharesMarketTabLabel icon={tab.icon} label={tab.label} count={counts[tab.countKey] ?? 0} />
          </button>
        );
      })}
      {ROUTE_TABS.map((tab) => {
        const active = tab.isActive(location.pathname);
        const count = counts[tab.countKey] ?? 0;
        return (
          <Link
            key={tab.path}
            to={tab.path}
            role="tab"
            aria-selected={active}
            className={`invx-tab invx-tab--link${active ? " active" : ""}`}
          >
            <SharesMarketTabLabel icon={tab.icon} label={tab.label} count={count} showCount={count > 0} />
          </Link>
        );
      })}
    </div>
  );
}
